import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { SelectBox } from "devextreme-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "src/contexts/auth";
import { fetchWithToken } from "src/hooks/useFetch";
import { SpatialRun } from "../types";
import FlowcellTileSelector from "./FlowcellTileSelector";
import { KIT_TYPE_TISSUE, WELL_LAYOUT_PANOS_SLIDE } from "./well-layout";
import {
  telemetryInstrumentFolder,
  telemetryLogsRootFrom,
} from "src/utils/telemetry-logs-path";

const bucket = "elembio-prod-usw2-s3-aviti-telemetry-logs-usw2";

/** e.g. L1R02C02S1_sequencing.mp4 / .webm → L1R02C02S1 */
function parseMovieFilenameToTileId(filename: string): string | null {
  const m = filename.match(/^(.+)_sequencing\.(mp4|webm)$/i);
  return m ? m[1] : null;
}

function nearestRunTileName(
  layoutTile: { XMillimeters: number; YMillimeters: number },
  runTiles: { Name: string; XMillimeters?: number; YMillimeters?: number }[],
): string {
  if (runTiles.length === 0) return "";
  let bestName = runTiles[0].Name;
  let bestDistance = Number.POSITIVE_INFINITY;
  runTiles.forEach((candidate) => {
    const dx = Math.abs(
      (candidate.XMillimeters ?? 0) - layoutTile.XMillimeters,
    );
    const dy = Math.abs(
      (candidate.YMillimeters ?? 0) - layoutTile.YMillimeters,
    );
    const distance = dx + dy;
    if (distance < bestDistance) {
      bestDistance = distance;
      bestName = candidate.Name;
    }
  });
  return bestName;
}

type CytoCycleProgressMovieProps = {
  run: SpatialRun;
};

export default function CytoCycleProgressMovie({
  run,
}: CytoCycleProgressMovieProps) {
  const { getAccessTokenSilently } = useAuth();
  const [selectedWell, setSelectedWell] = useState<string | null>(null);
  const [selectedTile, setSelectedTile] = useState<string | null>(null);
  const [selectedBatch, setSelectedBatch] = useState<string>("");
  const [movieURL, setMovieURL] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resolvedMoviePath, setResolvedMoviePath] = useState<string | null>(
    null,
  );
  const [playbackMime, setPlaybackMime] = useState<string>("video/webm");
  const objectURLRef = useRef<string | null>(null);
  const movieBlobRef = useRef<Blob | null>(null);
  const [movieTileIds, setMovieTileIds] = useState<Set<string>>(new Set());

  const {
    tenantID,
    instrumentID,
    instrumentSerialNumber,
    runID,
    wellLayout,
    wells,
    batches,
    kitType,
  } = run || {};

  const layoutType = useMemo(() => {
    if (wellLayout) return wellLayout;
    // Only reached for runs with no stored wellLayout. Tissue kits only run on
    // single-well flow cells and default to PanosSlide, so they must never fall
    // through to the 12-well default. Any other single-well run is a OneWell
    // (Mega Well); PanosSlide is not valid for non-tissue kits.
    const wellCount = wells?.length || 0;
    if (kitType === KIT_TYPE_TISSUE) return WELL_LAYOUT_PANOS_SLIDE;
    if (wellCount === 1) return "OneWell";
    if (wellCount === 48) return "FortyEightWell";
    return "TwelveWellStandard";
  }, [wellLayout, wells?.length, kitType]);

  const batchOptions = useMemo(() => {
    return (batches || [])
      .filter((batch) => batch.Name.startsWith("B"))
      .map((batch) => batch.Name);
  }, [batches]);

  const runPrefixNoBucket = useMemo(() => {
    let tID = tenantID;
    if (tID === "") tID = "ten_traefikee";
    return `${telemetryLogsRootFrom(run)}/${tID}/${telemetryInstrumentFolder(instrumentID, instrumentSerialNumber)}${
      runID ? `/runs/${runID}` : ""
    }`;
  }, [run, tenantID, instrumentID, instrumentSerialNumber, runID]);

  /** List S3 movie filenames for the current batch → set of tile ids (for selector highlighting). */
  useEffect(() => {
    if (!selectedBatch || !runPrefixNoBucket) {
      setMovieTileIds(new Set());
      return;
    }
    let cancelled = false;
    const dirs = [`Movies/${selectedBatch}`, `output/Movies/${selectedBatch}`];

    const load = async () => {
      const ids = new Set<string>();
      for (const dir of dirs) {
        try {
          const listResponse = await fetchWithToken(
            getAccessTokenSilently,
            "/run/logs",
            {
              method: "POST",
              body: JSON.stringify({
                Path: dir,
                Bucket: bucket,
                Prefix: runPrefixNoBucket,
              }),
            },
          );
          if (cancelled) return;
          const entries = Array.isArray(listResponse) ? listResponse : [];
          const names = entries
            .map((entry) => {
              if (typeof entry === "string") return entry;
              if (entry && typeof entry === "object" && "name" in entry) {
                const maybeName = (entry as { name?: unknown }).name;
                return typeof maybeName === "string" ? maybeName : null;
              }
              return null;
            })
            .filter((name): name is string => !!name);
          names.forEach((name) => {
            const id = parseMovieFilenameToTileId(name);
            if (id) ids.add(id);
          });
        } catch {
          // ignore; other dir may work
        }
      }
      if (!cancelled) setMovieTileIds(ids);
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [selectedBatch, runPrefixNoBucket, getAccessTokenSilently]);

  useEffect(() => {
    setSelectedWell(null);
    setSelectedTile(null);
    setMovieURL(null);
    setError(null);
    setResolvedMoviePath(null);
    setPlaybackMime("video/webm");
    movieBlobRef.current = null;
    setSelectedBatch((prev) =>
      prev && batchOptions.includes(prev) ? prev : batchOptions[0] ?? "",
    );
  }, [runID, batchOptions]);

  useEffect(() => {
    if (objectURLRef.current) {
      URL.revokeObjectURL(objectURLRef.current);
      objectURLRef.current = null;
    }
    movieBlobRef.current = null;

    if (!selectedTile || !selectedBatch || !runPrefixNoBucket) {
      setMovieURL(null);
      setError(null);
      setResolvedMoviePath(null);
      return;
    }

    let isCancelled = false;
    const candidateDirs = [
      `Movies/${selectedBatch}`,
      `output/Movies/${selectedBatch}`,
    ];

    setIsLoading(true);
    setError(null);
    setResolvedMoviePath(null);

    const loadMovie = async () => {
      for (const dir of candidateDirs) {
        try {
          const listResponse = await fetchWithToken(
            getAccessTokenSilently,
            "/run/logs",
            {
              method: "POST",
              body: JSON.stringify({
                Path: dir,
                Bucket: bucket,
                Prefix: runPrefixNoBucket,
              }),
            },
          );
          if (isCancelled) return;
          const entries = Array.isArray(listResponse) ? listResponse : [];
          const names = entries
            .map((entry) => {
              if (typeof entry === "string") return entry;
              if (entry && typeof entry === "object" && "name" in entry) {
                const maybeName = (entry as { name?: unknown }).name;
                return typeof maybeName === "string" ? maybeName : null;
              }
              return null;
            })
            .filter((name): name is string => !!name);

          const selectedTileLower = selectedTile.toLowerCase();
          const targetFile = names.find((name) => {
            const lower = name.toLowerCase();
            const isMovie = lower.endsWith(".webm") || lower.endsWith(".mp4");
            return isMovie && lower.startsWith(selectedTileLower);
          });
          if (!targetFile) continue;

          const fullPath = `${bucket}/${runPrefixNoBucket}/${dir}/${targetFile}`;
          const res = await fetchWithToken(
            getAccessTokenSilently,
            "/thumbnailBytes",
            {
              method: "POST",
              body: JSON.stringify({ path: fullPath }),
            },
          );
          if (isCancelled) return;
          if (!(res instanceof Blob) || res.size === 0) continue;
          const lowerName = targetFile.toLowerCase();
          const mime = lowerName.endsWith(".mp4") ? "video/mp4" : "video/webm";
          const normalizedVideoBlob = new Blob([res], { type: mime });

          const objectURL = URL.createObjectURL(normalizedVideoBlob);
          objectURLRef.current = objectURL;
          movieBlobRef.current = normalizedVideoBlob;
          setPlaybackMime(mime);
          setMovieURL(objectURL);
          setError(null);
          setResolvedMoviePath(`s3://${fullPath}`);
          return;
        } catch (err) {
          // Try next candidate path.
          console.warn("Movie lookup failed in dir:", dir, err);
        }
      }
      if (isCancelled) return;
      setMovieURL(null);
      setError("Movie is not available");
    };

    loadMovie()
      .catch((err) => {
        if (isCancelled) return;
        console.error("Failed to load cycle progress movie:", err);
        setMovieURL(null);
        setError("Movie is not available");
      })
      .finally(() => {
        if (!isCancelled) setIsLoading(false);
      });

    return () => {
      isCancelled = true;
      if (objectURLRef.current) {
        URL.revokeObjectURL(objectURLRef.current);
        objectURLRef.current = null;
      }
    };
  }, [runPrefixNoBucket, selectedBatch, selectedTile, getAccessTokenSilently]);

  useEffect(() => {
    return () => {
      if (objectURLRef.current) {
        URL.revokeObjectURL(objectURLRef.current);
        objectURLRef.current = null;
      }
    };
  }, []);

  const handleWellSelect = (wellLocation: string) => {
    setSelectedWell(wellLocation);
    setSelectedTile(null);
    setResolvedMoviePath(null);
    setMovieURL(null);
    setPlaybackMime("video/webm");
    movieBlobRef.current = null;
  };

  const tileHasMovie = useMemo(() => {
    return ({
      tileName,
      wellLocation,
      tile,
    }: {
      tileName: string;
      wellLocation: string;
      tile: { XMillimeters: number; YMillimeters: number };
    }) => {
      if (movieTileIds.size === 0) return false;
      const runWell = (wells || []).find(
        (w) => w.WellLocation === wellLocation,
      );
      const runTiles = runWell?.Tiles || [];
      const effectiveName =
        runTiles.length > 0 ? nearestRunTileName(tile, runTiles) : tileName;
      return movieTileIds.has(effectiveName);
    };
  }, [movieTileIds, wells]);

  const handleTileSelect = (
    tileName: string,
    wellLocation: string,
    tile?: { XMillimeters: number; YMillimeters: number },
  ) => {
    setSelectedWell(wellLocation);
    const runWell = (run?.wells || []).find(
      (w) => w.WellLocation === wellLocation,
    );
    const runTiles = runWell?.Tiles || [];

    if (tile && runTiles.length > 0) {
      let bestTileName = tileName;
      let bestDistance = Number.POSITIVE_INFINITY;
      runTiles.forEach((candidate) => {
        const dx = Math.abs((candidate.XMillimeters ?? 0) - tile.XMillimeters);
        const dy = Math.abs((candidate.YMillimeters ?? 0) - tile.YMillimeters);
        const distance = dx + dy;
        if (distance < bestDistance) {
          bestDistance = distance;
          bestTileName = candidate.Name;
        }
      });
      setSelectedTile(bestTileName);
      return;
    }

    setSelectedTile(tileName);
  };

  const handleDownloadBlob = () => {
    const blob = movieBlobRef.current;
    if (!blob || !selectedTile || !selectedBatch) return;
    const ext = playbackMime.includes("mp4") ? "mp4" : "webm";
    const fileName = `${selectedTile}_${selectedBatch}_sequencing.${ext}`;
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Box display="flex" gap={4} alignItems="flex-start">
      <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
        <FlowcellTileSelector
          wellLayout={layoutType}
          selectedWell={selectedWell}
          selectedTile={selectedTile}
          onWellSelect={handleWellSelect}
          onTileSelect={handleTileSelect}
          tileHasAuxiliaryHighlight={tileHasMovie}
        />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ maxWidth: 320 }}
        >
          Green tiles: movie file present for this batch
        </Typography>
      </Box>

      <Box
        display="flex"
        flexDirection="column"
        alignItems="stretch"
        sx={{ minWidth: 560 }}
      >
        <Box
          display="flex"
          alignItems="center"
          gap={2}
          sx={{ marginBottom: 2, flexWrap: "wrap" }}
        >
          <Typography variant="body1" fontWeight={600}>
            Batch:
          </Typography>
          <SelectBox
            value={selectedBatch}
            items={batchOptions}
            onValueChanged={(e) => setSelectedBatch(e.value || "")}
            width={120}
          />
          <Typography variant="body2" color="text.secondary">
            {selectedTile
              ? `${selectedWell} - ${selectedTile}`
              : "Select a tile"}
          </Typography>
        </Box>
        {resolvedMoviePath && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", marginBottom: 1 }}
          >
            {resolvedMoviePath}
          </Typography>
        )}
        {movieURL && (
          <Button
            variant="outlined"
            size="small"
            onClick={handleDownloadBlob}
            sx={{ width: "fit-content", marginBottom: 1 }}
          >
            Download movie
          </Button>
        )}

        <Box
          sx={{
            minHeight: 420,
            minWidth: 560,
            maxWidth: 920,
            borderRadius: 1,
            border: "1px solid #ddd",
            backgroundColor: "#000",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 2,
          }}
        >
          {isLoading ? (
            <CircularProgress sx={{ color: "#fff" }} />
          ) : error ? (
            <Typography sx={{ color: "#fff" }}>{error}</Typography>
          ) : movieURL ? (
            <video
              key={movieURL}
              controls
              preload="metadata"
              style={{ width: "100%", maxHeight: 620, background: "#000" }}
              onError={(event) => {
                setError("Movie is not available");
              }}
            >
              <source src={movieURL} type={playbackMime} />
              Your browser cannot play this video.
            </video>
          ) : (
            <Typography sx={{ color: "#aaa" }}>
              Select a batch and tile to view the cycle progress movie.
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}
